import { Col, Form, Row } from 'antd';
import React, { useEffect } from 'react';
import { useMappingForm, useSelectedField } from '../../../../../utils/zustand';
import LanguageSelector from './LanguageSelector';
import MappingChain from './MappingChain';
import MappingType from './MappingType';
import MappingUnique from './MappingUnique';
import TargetField from './TargetField';

const MappingForm = () => {
  const [form] = Form.useForm();
  const { selectedField, setSelectedField } = useSelectedField();
  const { setFormInstance, clearFormInstance } = useMappingForm();

  // Enregistrer l'instance du formulaire dans le store Zustand
  useEffect(() => {
    setFormInstance(form);
    return () => clearFormInstance();
  }, [form, setFormInstance, clearFormInstance]);

  // --- GLOBAL STATE HANDLERS ---
  // We now update `selectedField` directly which serves as the source of truth.
  // The structure must match the one produced by formatToUI.

  const handleUpdateSelectedField = (key, value) => {
    setSelectedField({ ...selectedField, [key]: value });
  };

  // Handler for Mapping Type Switch
  const handleTypeChange = (typeValue) => {
    // Check if we need to init specific structure for new type if missing
    const updatedField = {
      ...selectedField,
      transformation_type: typeValue,
    };

    // Ensure the nested structure exists if it's null
    if (typeValue === 'unique' && !updatedField.transformation?.unique) {
      updatedField.transformation = {
        ...updatedField.transformation,
        unique: { condition: null, transformation: { type: null, params: {} } },
      };
    }

    if (typeValue === 'chain' && !updatedField.transformation?.chain) {
      updatedField.transformation = {
        ...updatedField.transformation,
        chain: {
          steps: [],
          default_transformation: { type: null, params: {} },
        },
      };
    }

    if (typeValue === 'simple' && !updatedField.transformation?.simple) {
      updatedField.transformation = {
        ...updatedField.transformation,
        simple: { source_field: null },
      };
    }

    setSelectedField(updatedField);
  };

  // --- SUB-COMPONENT HANDLERS ---

  const handleUniqueChange = (newData) => {
    // newData = { condition: ..., transformation: ... }
    const newTransformation = {
      ...selectedField.transformation,
      unique: newData,
    };
    setSelectedField({ ...selectedField, transformation: newTransformation });
  };

  const handleChainChange = (newData) => {
    // newData = { steps: [...], default_transformation: ... }
    const newTransformation = {
      ...selectedField.transformation,
      chain: newData,
    };
    setSelectedField({ ...selectedField, transformation: newTransformation });
  };

  const handleSimpleChange = (sourceField) => {
    const newTransformation = {
      ...selectedField.transformation,
      simple: { source_field: sourceField },
    };
    setSelectedField({ ...selectedField, transformation: newTransformation });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      validateTrigger={['onChange', 'onBlur']}
    >
      <Row gutter={[16, 16]}>
        {/* HEADER: Type + Lang + Target */}
        <Col span={18}>
          <MappingType
            mappingType={selectedField.transformation_type}
            onTypeChange={handleTypeChange}
          />
        </Col>
        <Col span={6}>
          <Form.Item
            label="Langue de destination"
            name="lang"
            initialValue={selectedField?.lang}
            rules={[
              { required: true, message: 'Veuillez sélectionner une langue' },
            ]}
          >
            <LanguageSelector
              selectedLanguage={selectedField?.lang}
              onChange={(value) => handleUpdateSelectedField('lang', value)}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <TargetField
            selectedField={selectedField}
            handleUpdateSelectedField={handleUpdateSelectedField}
            onSourceFieldChange={handleSimpleChange}
          />
        </Col>
        <Col span={24}></Col>
        {/* BODY: Dynamic Configuration based on Type */}
        <Col span={24}>
          {selectedField.transformation_type === 'unique' && (
            <MappingUnique
              mappingData={selectedField.transformation?.unique || {}}
              onChange={handleUniqueChange}
            />
          )}

          {selectedField.transformation_type === 'chain' && (
            <MappingChain
              mappingData={selectedField.transformation?.chain || {}}
              onChange={handleChainChange}
            />
          )}

          {selectedField.transformation_type === 'none' && (
            <div style={{ padding: 20, textAlign: 'center', color: '#999' }}>
              Ce champ ne sera pas exporté.
            </div>
          )}
        </Col>
      </Row>
    </Form>
  );
};

export default MappingForm;
